import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import DataGrid, {
  Column,
  Lookup,
  Editing,
  RangeRule,
  Button,
  RequiredRule,
  Toolbar,
  Item,
} from "devextreme-react/data-grid";
import moment from "moment";
import { OidcIdentityContext } from "@rsmus/react-auth";
import {
  BooleanValue,
  GetScheduleFiles,
  RunOnUpdatedSiteSelect,
  ScheduleStatus,
  ServiceBusEnum,
  SyncTypeEnum,
  TimeUnit,
} from "../../api-client-nswag/taxportal-client";
import { dataGridConfig, jobTypesWithFrequencyDisabled, jobTypesWithoutLogDetails, JobTypesWithoutSelection } from "../../constants";
import { reducer } from "./reducer/reducer";
import { useRecoilValue } from "recoil";
import {
  fetchData,
  applyChanges,
  updateChanges,
  setEditRowKey,
} from "./reducer/actions";
import { convertUTCTimeToLocal, selectValues } from "../../util/Utils";
import { IChangeProps } from "devextreme-react/tree-list";
import useSnackbar from "../../hooks/useSnackbar";
import { ViewExecutionLogs } from "../../cui/components/ViewExecutionLogs/ViewExecutionLogs";
import ModalUnsavedWarning from "../../cui/components/Modal/ModalUnsavedWarning";
import AppNameToggle, { getAppName } from "../../cui/commonComponents/AppNameToggle";
import { SiteSelectionPopup } from "./SiteSelectionPopup";
import { CellDateTimePicker } from "../../cui/components/CellDatePicker/CellDateTimePicker";
import {
  constants,
  EditModeType,
  InitialScheduleStateProps,
  SelectOption,
  TAX_PORTAL,
} from "./ScheduleFiles.types";

type DatagridRefType = DataGrid;

const {
  APP,
  SCHEDULE_NAME,
  TIME,
  FREQUENCY,
  START_EXECUTION_DATE,
  LAST_EXECUTION,
  LAST_EXECUTION_DATE,
  NEXT_EXECUTION_DATE,
  LOG_DETAILS,
  GRID_DATE_TIME_FORMAT,
  MODIFIED_BY,
} = constants;

const initialScheduleState: InitialScheduleStateProps = {
  data: [],
  changes: [],
  editRowKey: null,
  editRow: null,
  isLoading: false,
  userName: "",
};

const values = [TimeUnit.MINUTES, TimeUnit.HOURS];
const status = [ScheduleStatus.SCHEDULED, ScheduleStatus.STOPPED];
const runOnUpdate = [RunOnUpdatedSiteSelect.YES, RunOnUpdatedSiteSelect.NO, RunOnUpdatedSiteSelect.SELECT];
const statusOptions: SelectOption[] = selectValues(status);
const RunOnUpdateOptions: SelectOption[] = runOnUpdate.map((option) => ({ ID: option, Name: option }));
const RunOnUpdateYesNoOptions: SelectOption[] = [RunOnUpdatedSiteSelect.YES, RunOnUpdatedSiteSelect.NO].map((option) => ({ ID: option, Name: option }));
const frequencyOptions: SelectOption[] = selectValues(values);

const showLogDetails = (e: { row: { data: GetScheduleFiles } }) =>
  !jobTypesWithoutLogDetails.includes(e.row.data.displayName);

export const displayNameToSyncTypeMap: Record<string, SyncTypeEnum> = {
  "Create External Folders": SyncTypeEnum.EXTERNAL_FOLDER_CREATION,
  "(MS) Check External User Status": SyncTypeEnum.CHECK_EXTERNAL_USER_STATUS,
  "(MS) Check External User Status - CS": SyncTypeEnum.CHECK_EXTERNAL_USER_STATUS,
  "(MS) Sync CEM": SyncTypeEnum.SYNC_CEM,
  "(MS) Sync MDM Project & Phase": SyncTypeEnum.SYNC_MDM_PROJECT,
  "(MS) Create Folder With Permissions & Metadata": SyncTypeEnum.EXTERNAL_FOLDER_CREATION,
  "(MS) Rollover Archive Cleanup": SyncTypeEnum.ROLLOVER_ARCHIVE_CLEANUP,
  "(MS) Update Project Status ISheet": SyncTypeEnum.SYNC_CEM_HQ,
  "(MS) Push Org To ISheet": SyncTypeEnum.PUSH_ORG_TO_ISHEET,
  "(MS) Site Metrics IR Record Count": SyncTypeEnum.SITE_METRICS_IR_RECORD_COUNT,
  "(MS) Site Metrics PS Record Count": SyncTypeEnum.SITE_METRICS_PS_RECORD_COUNT,
  "(MS) Site Metrics New Opportunities Record Count": SyncTypeEnum.SITE_METRICS_NEW_OPPORTUNITIES_RECORD_COUNT,
  "(MS) Site Metrics K-1 Tracker Record Count": SyncTypeEnum.SITE_METRICS_K1_TRACKER_RECORD_COUNT,
  "(MS) Site Metrics Cash Tax Payments Record Count": SyncTypeEnum.SITE_METRICS_CASH_TAX_PAYMENTS_RECORD_COUNT,
  "(MS) Site Metrics Controversy Tracker Record Count": SyncTypeEnum.SITE_METRICS_CONTROVERSY_TRACKER_RECORD_COUNT,
  "(MS) Site Metrics Issues Tracker Record Count": SyncTypeEnum.SITE_METRICS_ISSUES_TRACKER_RECORD_COUNT,
  "(MS) Site Metrics Notice Tracker Record Count": SyncTypeEnum.SITE_METRICS_NOTICE_TRACKER_RECORD_COUNT,
  "(MS) Site Metrics Estimates Tracker Record Count": SyncTypeEnum.SITE_METRICS_ESTIMATES_TRACKER_RECORD_COUNT,
  "(MS) Site Metrics Deliverable Listing Record Count": SyncTypeEnum.SITE_METRICS_DELIVERABLE_LISTING_RECORD_COUNT,
  "(MS) Site Metrics Engagement Overview Record Count": SyncTypeEnum.SITE_METRICS_ENGAGEMENT_OVERVIEW_RECORD_COUNT,
  "(MS) Sync Deliverables": SyncTypeEnum.SYNC_DELIVERABLES,
  "(MS) Sync Document Metadata": SyncTypeEnum.SYNC_DM,
  "(MS) Sync Information Request": SyncTypeEnum.SYNC_IR,
  "(MS) Sync K-1 Tracker": SyncTypeEnum.SYNC_K1_TRACKER,
  "(MS) Sync Project Status": SyncTypeEnum.SYNC_PS,
  "(MS) Sync ISheet": SyncTypeEnum.SYNC_ISHEET,
  "(MS) Update Document Count": SyncTypeEnum.UPDATE_DOCUMENT_COUNT,
  "(MS) Site Metrics Deliverables Record Count": SyncTypeEnum.SITE_METRICS_DELIVERABLES_RECORD_COUNT,

  "(MS) Rollover Retrival Storage Cleanup": SyncTypeEnum.ROLLOVER_RETRIEVAL_STORAGE_CLEANUP,

  "(MS) Update TP Due Dates": SyncTypeEnum.UPSERT_DUEDATE,
  "(MS) Update TP Global Due Dates": SyncTypeEnum.UPSERT_GLOBAL_DUEDATE,
  "(MS) Update TP Site Tasks": SyncTypeEnum.UPSERT_SITE_TASKS,
  "(MS) Get Yearly OIT Efile Report By Client": SyncTypeEnum.YEARLY_OIT_EFILE_REPORT,
  "(MS) Update TP Efile Statuses": SyncTypeEnum.GET_OIT_EFILE_RESOURCES,
  "(MS) Update TP Project Status": SyncTypeEnum.UPSERT_ISHEET_ALL_PROJECTS_METADATA,
  "(MS) Update TP Analytics Data": SyncTypeEnum.UPSERT_ISHEET_ANALYTIC_DATA,
  "(MS) Update TP Analytics Insights": SyncTypeEnum.UPSERT_ISHEET_ANALYTIC_INSIGHT_SETUP,
  "(MS) Update RTP Analytics": SyncTypeEnum.UPSERT_ISHEET_RTP_DATA_ANALYTICS,
  "(MS) Update TP Tax Summary": SyncTypeEnum.UPSERT_ISHEET_TAX_SUMMARY_FORM,
  "(MS) Update TP Planning Ideas": SyncTypeEnum.UPSERT_ISHEET_PLANNING_IDEAS,
  "(MS) Update TP Cash Tax Projections": SyncTypeEnum.UPSERT_ISHEET_CASH_TAX_PROJECTIONS,
  "(MS) Update TP Cash Tax Payments": SyncTypeEnum.UPSERT_ISHEET_CASH_TAX_PAYMENTS,
  "(MS) Update TP Cash Tax Estimates": SyncTypeEnum.UPSERT_ISHEET_CASH_TAX_ESTIMATES_TRACKER,
  "(MS) Get LiveApi": SyncTypeEnum.GET_LIVE_API,
  "(MS) Get Binders": SyncTypeEnum.UPSERT_BINDERS,
};

export const ScheduleFiles = () => {
  const scheduleFilesGridRef = useRef<DatagridRefType>(null);
  const { user } = useContext(OidcIdentityContext);
  const userName = user?.profile.preferred_username!;
  const { showSnackbar } = useSnackbar();
  const appName = useRecoilValue(getAppName);
  const minSelectableDate = new Date();
  minSelectableDate.setHours(0, 0, 0, 0);

  const [scheduleState, dispatch] = useReducer(reducer, initialScheduleState);
  const [updateState, updateDispatch] = useReducer(reducer, initialScheduleState);
  const [selectedTypeNumber, setSelectedTypeNumber] = useState<number>();
  const [isDisplayModal, setIsDisplayModal] = useState<boolean>(false);
  const [isSitePopupOpen, setIsSitePopupOpen] = useState<boolean>(false);
  const pendingSiteIdsRef = useRef<number[]>([]);
  const pendingSiteIdsRowKeyRef = useRef<number | null>(null);
  const [saveMode, setSaveMode] = useState<EditModeType>({
    isEditing: false,
    isSaving: false,
  });

  useEffect(() => {
    fetchData(dispatch, userName, showSnackbar);
  }, []);

  const handleRefresh = () => fetchData(dispatch, userName, showSnackbar);

  const refreshButtonOptions = {
    icon: "refresh",
    onClick: handleRefresh,
  };

  const filteredSchedules = useMemo(() => {
    if (!scheduleState?.data) return [];

    return scheduleState.data.filter((e) =>
      e.appName === appName || e.appName === null
    );
  }, [scheduleState?.data, appName]);


  const forceDateFormat = (date: string) => {
    const val = moment(date);
    return val.isValid() ? val.utc().format(GRID_DATE_TIME_FORMAT).toString() : null;
  };

  const cellRenderForDate = useCallback((date: string) => {
    if (!date) return "";
    const value = typeof date !== "string" ? forceDateFormat(date) : date;
    if (!value) return "";
    return <div>{convertUTCTimeToLocal(value)}</div>;
  }, []);

  const onEditorPreparing = useCallback((e: any) => {

    if (e.parentType === "dataRow" && ["frequencyValue", "frequencyType"].includes(e.dataField) && jobTypesWithFrequencyDisabled.includes(e.row?.data?.displayName)) {
      e.editorOptions.disabled = true;
      return;
    }

    if (e.dataField === "runOnUpdatedSiteSelect" && e.parentType === "dataRow") {
      if (e.row?.data?.scheduledFunction === "GenericSiteIndependentTask" || JobTypesWithoutSelection.includes(e.row?.data?.displayName)) {
        e.editorOptions.readOnly = true;
        return;
      }
      if (
        e.row?.data?.typeNumber === ServiceBusEnum.USAGE_METRICS_REPORT ||
        e.row?.data?.typeNumber === ServiceBusEnum.UPLOAD_FILES
      ) {
        e.editorOptions.dataSource = RunOnUpdateYesNoOptions;
        return;
      }
      const originalOnValueChanged = e.editorOptions.onValueChanged;
      e.editorOptions.onValueChanged = (args: any) => {
        if (originalOnValueChanged) originalOnValueChanged(args);
      };
      e.editorOptions.onClosed = (args: any) => {
        const selectedValue = args.component.option("value");
        if (selectedValue === RunOnUpdatedSiteSelect.SELECT) {
          const rowKey = e.row?.key;
          // Only seed from row data when opening for a different row; preserve
          // already-selected IDs when the user re-opens the popup for the same row.
          if (pendingSiteIdsRowKeyRef.current !== rowKey) {
            const raw: number[] | null = e.row?.data?.scheduleConfigMessage?.siteIds ?? null;
            pendingSiteIdsRef.current = raw ? raw.map(Number).filter(Boolean) : [];
            pendingSiteIdsRowKeyRef.current = rowKey;
          }
          setIsSitePopupOpen(true);
        }
        e.setValue(selectedValue);
      };
    }
  }, []);

  const onEditRowKeyChange = useCallback((editRowKey: number) => {
    setEditRowKey(dispatch, editRowKey);
    setSaveMode({
      isEditing: true,
      isSaving: false,
    });
  }, []);

  const onChangesChange = useCallback((changes: IChangeProps[]) => {
    updateChanges(dispatch, changes);
  }, []);

  const onSaving = useCallback(
    (e: any, userName: string) => {
      e.cancel = true;
      if (e.changes.length > 0 && e.changes[0].data.runOnUpdatedSiteSelect !== undefined) {
        const rawValue = e.changes[0].data.runOnUpdatedSiteSelect;
        if (!e.changes[0].data.scheduleConfigMessage) {
          const originalRow = scheduleState.data.find((row) => row.id === e.changes[0].key);
          e.changes[0].data.scheduleConfigMessage = { ...(originalRow?.scheduleConfigMessage ?? {}) };
        }
        if (rawValue === RunOnUpdatedSiteSelect.SELECT) {
          if (pendingSiteIdsRef.current.length === 0) {
            showSnackbar({ message: "Please select at least one siteID.", severity: "warning" });
            return;
          }
          e.changes[0].data.runOnUpdatedSite = null;
          e.changes[0].data.scheduleConfigMessage.siteIds = pendingSiteIdsRef.current.length > 0 ? pendingSiteIdsRef.current : null;
        } else {
          e.changes[0].data.runOnUpdatedSite = rawValue === RunOnUpdatedSiteSelect.YES;
          e.changes[0].data.scheduleConfigMessage.siteIds = [];
        }
      }
      e.promise = applyChanges(dispatch, e.changes[0], scheduleState, userName);
      pendingSiteIdsRef.current = [];
      pendingSiteIdsRowKeyRef.current = null;
      setSaveMode({
        isEditing: false,
        isSaving: true,
      });
    },
    [scheduleState, updateState]
  );

  const handleOpenModal = (e: any) => {
    const { id } = e?.row?.data || {};

    setSelectedTypeNumber(id);
    setIsDisplayModal(true);
  };

  const handleCloseModal = () => {
    setSelectedTypeNumber(0);
    setIsDisplayModal(false);
  };

  const onEditCanceled = () => {
    pendingSiteIdsRef.current = [];
    pendingSiteIdsRowKeyRef.current = null;
    setSaveMode({
      isEditing: false,
      isSaving: true,
    });
  };

  const cellRender = (e: any) => {
    if (e.data.appName === null) {
      return appName;
    }
    return e.data.appName;
  };

  return (
    <div style={{ marginLeft: "20px", marginRight: "20px" }}>
      <div style={{ marginLeft: "3px", marginTop: "40px", display: "flex" }}>
        <h2>Schedule Executions</h2>
        <AppNameToggle />
      </div>
      <DataGrid
        id="gridContainer"
        keyExpr="id"
        ref={scheduleFilesGridRef}
        dataSource={filteredSchedules}
        {...dataGridConfig}
        noDataText={scheduleState.isLoading ? "Loading..." : "No data"}
        repaintChangesOnly
        onSaving={(e) => onSaving(e, userName)}
        onEditCanceled={onEditCanceled}
        onEditorPreparing={onEditorPreparing}
      >
        <Editing
          mode="row"
          allowUpdating
          useIcons
          changes={scheduleState.changes}
          onChangesChange={onChangesChange}
          editRowKey={scheduleState.editRowKey}
          onEditRowKeyChange={onEditRowKeyChange}
        />
        <Toolbar>
          <Item widget="dxButton" options={refreshButtonOptions} name="alignRight" />
        </Toolbar>
        <Column caption={APP} dataField="appName" cellRender={cellRender} width={20} allowEditing={false} />
        <Column dataField="id" allowEditing={false} width={20} />
        <Column caption={SCHEDULE_NAME} width={230} dataField="displayName" allowEditing={false} />
        <Column dataField="frequencyValue" width={30} caption={TIME}>
          <RequiredRule />
          <RangeRule min={1} message="Only values greater than 0 are allowed." />
        </Column>
        <Column caption={FREQUENCY} width={80} dataField="frequencyType" dataType="number">
          <RequiredRule />
          <Lookup dataSource={frequencyOptions} valueExpr="ID" displayExpr="Name" />
        </Column>
        <Column
          dataField="startExecution"
          width={190}
          caption={START_EXECUTION_DATE}
          dataType="date"
          cellRender={(e) => cellRenderForDate(e.data.startExecution)}
          editCellRender={({ value, setValue }) => (
            <CellDateTimePicker
              value={value}
              setValue={setValue}
              showTimeOptions
              minDate={minSelectableDate}
            />
          )}
        />
        <Column dataField="status" width={100}>
          <Lookup dataSource={statusOptions} valueExpr="ID" displayExpr="Name" />
        </Column>
        <Column dataField="runOnUpdatedSiteSelect" width={100} caption="Run On Updated Site">
          <Lookup dataSource={RunOnUpdateOptions} valueExpr="ID" displayExpr="Name" />
        </Column>
        <Column caption={LAST_EXECUTION} dataField="lastExecutionStatus" width={110} allowEditing={false} />
        <Column
          cellRender={(e) => cellRenderForDate(e.data.lastExecutionDateTime)}
          dataField="lastExecutionDateTime"
          width={200}
          caption={LAST_EXECUTION_DATE}
          allowEditing={false}
        />
        <Column
          cellRender={(e) => {
            const nextExecutionDate =
              e.data.status?.toLowerCase() === "scheduled"
                ? e.data.nextExecutionDateTime
                : null;

            return cellRenderForDate(nextExecutionDate);
          }}
          dataField="nextExecutionDateTime"
          width={200}
          caption={NEXT_EXECUTION_DATE}
          allowEditing={false}
        />
        <Column dataField="lastModifiedBy" width={200} caption={MODIFIED_BY} allowEditing={false} />
        <Column dataField="actions" type="buttons" width={80} caption={LOG_DETAILS}>
          <Button
            icon="file"
            onClick={handleOpenModal}
            visible={showLogDetails}
          />
        </Column>
        <Column type="buttons" width={80}>
          <Button name="edit" />
        </Column>
      </DataGrid>
      <ViewExecutionLogs
        optionsDropdown={filteredSchedules}
        optionSelected={selectedTypeNumber!}
        handleClose={handleCloseModal}
        isDisplayModal={isDisplayModal}
      />
      <ModalUnsavedWarning when={saveMode.isEditing && !saveMode.isSaving} />
      <SiteSelectionPopup
        isOpen={isSitePopupOpen}
        preSelectedIds={pendingSiteIdsRef.current}
        appName={appName}
        onClose={() => setIsSitePopupOpen(false)}
        onConfirm={(selectedIds) => {
          pendingSiteIdsRef.current = selectedIds;
          setIsSitePopupOpen(false);
        }}
      />
    </div>
  );
};
